import React, { useState, useEffect } from 'react';

// Material UI
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

// Components
import Dropdown from '../inputs/dropdown';
import TextField from '../inputs/input';
import Autocomplete from '../inputs/Autocomplete';
import Modal from '../modal';

// Hooks
import useUser from '../../hooks/user/useUser';
import useAddresses from '../../hooks/customers/useAddresses';

// Models
import { ShippingTypes, ShippingTypesList } from '../../models/ShippingTypes';
import { countries } from '../../models/EnumEntity';
import useCart from '../../hooks/orders/CartProvider';

const addressDraff = {
    state: '',
    province: '',
    country: '',
    zipCode: '',
    street: '',
    number: '',
    complement: ''
}

const ShippingModal = ({ onClose, open }) => {
    const { branch } = useUser();
    const { sale: { items, shippingData, customer }, warehouses, update } = useCart();
    const { addresses, loading, createAddress } = useAddresses(customer?.id);
    const [warehousesAvailable, setWarehousesAvailable] = useState([]);
    const [addressSelected, setAddressSelected] = useState(addressDraff);
    const [shippingType, setShippingType] = useState(ShippingTypes.Pickup);
    const [warehouseIdDestination, setWarehouseIdDestination] = useState(branch.warehouseId || '');

    const couriers = {
        AR: "EnvioPack",
        UY: "Ocasa"
    }

    useEffect(() => {
        const adIn = addresses?.filter(address => address.country === branch.country && address.isShipping)
        setAddressSelected(adIn[0] || {})
    }, [addresses, branch.country]);

    useEffect(() => {
        const getWarehousesSelected = () =>
            [...new Set([branch?.warehouseId, ...(items?.map(e => e.warehouseId) || [])])];

        if (branch?.warehouseId) {
            const warehousesSelected = getWarehousesSelected();

            if (warehouses && warehouses.length > 0 && warehousesSelected)
                setWarehousesAvailable(warehousesSelected.map(bs => warehouses.find(e => e.id === bs)));

            if (items?.some(e => e.warehouseId !== branch?.warehouseId))
                setShippingType(ShippingTypes.Pickup)             
        }
    }, [warehouses, items, branch?.warehouseId])

    useEffect(() => {
        if (shippingData) {
            setShippingType(shippingData.type);
            if (shippingData.type === ShippingTypes.Pickup) {
                setWarehouseIdDestination(shippingData.warehouseIdDestination)
            }

            if (shippingData.type === ShippingTypes.Delivery) {
                if (shippingData.address)
                    setAddressSelected(state => ({ ...state, ...shippingData.address }))
            }
        } else if (items?.some(e => e.warehouseId !== branch?.warehouseId))
            setWarehouseIdDestination()
    }, [shippingData, branch]);

    const handleAddressChange = (event) => {
        setAddressSelected(state => ({
            ...state,
            [event.target.name]: event.target.value
        }));
    }

    const handleSelectAddress = (event, option) => {
        setAddressSelected(option || addressDraff)
    }

    const handleTypeChange = (event) => {
        const type = event.target.value;
        setShippingType(type);
    }

    const handleWarehouseDestination = (event) => {
        const warehouse = warehousesAvailable.find(b => b.id === event.target.value);
        setWarehouseIdDestination(warehouse.id)
    }

    const handleSave = () => {
        if (shippingType === ShippingTypes.Pickup) {
            const shippingData = {
                type: shippingType,
                warehouseIdDestination,
                courier: 'none'
            }

            update({ shippingData });
        }

        if (shippingType === ShippingTypes.Delivery) {
            if (!addresses || !addresses.length)
                createAddress(addressSelected);
            const shippingData = {
                type: shippingType,
                courier: couriers[branch.country], //Asi accedes a la propiedad con el pais
                address: addressSelected,
                warehouseIdDestination
            }

            update({ shippingData });
        }

        onClose();
    }

    const isDisabledSabe = () => {
        if (shippingType === ShippingTypes.Delivery)
            return (
                !addressSelected ||
                !addressSelected.state ||
                !addressSelected.province ||
                !addressSelected.country ||
                !addressSelected.zipCode ||
                !addressSelected.street ||
                !addressSelected.number
            );
        else
            return !warehouseIdDestination;
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title='Método de envío'
            contentProps={{ dividers: true }}
            customButtons={() =>
                <DialogActions>
                    <Button
                        onClick={onClose}
                        color="primary"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant='outlined'
                        disabled={isDisabledSabe()}
                    >
                        Guardar
                    </Button>
                </DialogActions>}
        >
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Dropdown
                        fullWidth
                        name='type'
                        label='Tipo'
                        SelectDisplayProps={{ 'data-testid': 'shippingTypeId' }}
                        onChange={handleTypeChange}
                        value={shippingType || ''}
                        values={ShippingTypesList}
                        disabled
                    />
                </Grid>
                {shippingType === ShippingTypes.Delivery &&
                    <>
                        <Grid size={12}>
                            <Autocomplete
                                required
                                disabled
                                loading={loading}
                                loadingText="Buscando direcciones..."
                                onChange={handleSelectAddress}
                                value={addressSelected}
                                options={addresses?.filter(address => address.country === branch.country && address.isShipping) || []}
                                getOptionLabel={(option) => option.id ? option.fullAddress : ''}
                                renderOption={(option) => option.id ? option.fullAddress : ''}
                                TextProps={{
                                    label: 'Dirección',
                                    placeholder: 'Ej.: Sarmiento 4034, CABA',
                                    helperText: 'Empezá a escribir para listar las direcciones'
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Dropdown
                                disabled
                                fullWidth
                                required
                                SelectDisplayProps={{ 'data-testid': 'contryId' }}
                                name='country'
                                label='País'
                                value={addressSelected.country || ''}
                                onChange={handleAddressChange}
                                values={countries}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                disabled
                                fullWidth
                                required
                                name='state'
                                label='Provincia'
                                onChange={handleAddressChange}
                                value={addressSelected.state || ''}
                            />
                        </Grid>
                        <Grid size={8}>
                            <TextField
                                disabled
                                fullWidth
                                required
                                name='city'
                                label='Localidad'
                                onChange={handleAddressChange}
                                value={addressSelected.city || ''}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                disabled
                                fullWidth
                                required
                                name='zipCode'
                                label='Código postal'
                                onChange={handleAddressChange}
                                value={addressSelected.zipCode || ''}
                            />
                        </Grid>
                        <Grid size={8}>
                            <TextField
                                disabled
                                fullWidth
                                required
                                name='street'
                                label='Calle'
                                onChange={handleAddressChange}
                                value={addressSelected.street || ''}
                            />
                        </Grid>
                        <Grid size={4}>
                            <TextField
                                disabled
                                fullWidth
                                required
                                name='number'
                                label='Número'
                                onChange={handleAddressChange}
                                value={addressSelected.number || ''}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                disabled
                                fullWidth
                                name='floor'
                                label='Piso'
                                onChange={handleAddressChange}
                                value={addressSelected.floor || ''}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                disabled
                                fullWidth
                                name='apartment'
                                label='Departamento'
                                onChange={handleAddressChange}
                                value={addressSelected.apartment || ''}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                disabled
                                fullWidth
                                multiline
                                rows={4}
                                name='complement'
                                label='Información complementaria'
                                onChange={handleAddressChange}
                                value={addressSelected.observation || ''}
                            />
                        </Grid>
                    </>}
                {shippingType === ShippingTypes.Pickup &&
                    <Grid size={12}>
                        <Dropdown
                            fullWidth
                            name='branchDestination'
                            label='Sucursal'
                            value={warehouseIdDestination || ''}
                            onChange={handleWarehouseDestination}
                            values={warehousesAvailable}
                            getOptionLabel={(option) => option?.name}
                            getOptionValue={(option) => option?.id}
                            SelectDisplayProps={{ 'data-testid': 'branchDestinationId' }}
                        />
                    </Grid>}
            </Grid>
        </Modal>
    );
}

export default ShippingModal;